import { amplitudeLib, FlowType } from 'freedom-shared';

import {
  type AnimatedFlowVariant,
  getAnimatedFlowVariant,
} from '@/widgets/search-form/model/use-animated-flow-experiment';

export type AnimatedFlowField = 'dep' | 'arr' | 'dates' | 'passengers';

export type AnimatedFlowFieldOpenTrigger =
  | 'sheet_open'
  | 'auto_advance'
  | 'chip_tap';

/**
 * Fired once per session when the animated-flow experiment finishes loading
 * and the user is bucketed into a known variant. Mirrors `quickBookingAssigned`.
 */
export const animatedFlowAssigned = (variant: AnimatedFlowVariant) => {
  amplitudeLib.logEvent('avfree_animated_flow_assigned', {
    variant,
    app: FlowType.AVIA,
  });
};

/**
 * Fired every time a field surface becomes active inside the unified sheet —
 * either because the sheet just opened (`sheet_open`), because a previous step
 * auto-advanced into the next field (`auto_advance`), or because the user tapped
 * a chip to jump to a different field (`chip_tap`).
 */
export const animatedFlowFieldOpen = (params: {
  field: AnimatedFlowField;
  initial_field: AnimatedFlowField;
  source: string;
  trigger: AnimatedFlowFieldOpenTrigger;
}) => {
  amplitudeLib.logEvent('avfree_animated_flow_field_open', {
    field: params.field,
    initial_field: params.initial_field,
    source: params.source,
    trigger: params.trigger,
    variant: getAnimatedFlowVariant(),
    app: FlowType.AVIA,
  });
};

/**
 * Fired when the user commits a value for a given field (city picked, dates
 * confirmed, passengers applied). `duration_ms` measures time spent on that
 * field surface from the matching `field_open` event.
 */
export const animatedFlowFieldCommit = (params: {
  field: AnimatedFlowField;
  source: string;
  duration_ms: number;
}) => {
  amplitudeLib.logEvent('avfree_animated_flow_field_commit', {
    field: params.field,
    source: params.source,
    duration_ms: params.duration_ms,
    variant: getAnimatedFlowVariant(),
    app: FlowType.AVIA,
  });
};

/**
 * Fired when the unified sheet closes without the user reaching submit (drag
 * down, close button, hardware back). `fields_completed` lists the steps that
 * were already committed before the dismiss — used for per-step drop-off.
 */
export const animatedFlowDismissed = (params: {
  last_field: AnimatedFlowField;
  fields_completed: AnimatedFlowField[];
  source: string;
  duration_ms: number;
}) => {
  amplitudeLib.logEvent('avfree_animated_flow_dismissed', {
    last_field: params.last_field,
    fields_completed: params.fields_completed,
    source: params.source,
    duration_ms: params.duration_ms,
    variant: getAnimatedFlowVariant(),
    app: FlowType.AVIA,
  });
};
